export type SmartVideoRuntimeReviewKind = 'main' | 'short';

export interface SmartVideoRuntimeReviewCheckpoint {
  kind: SmartVideoRuntimeReviewKind;
  index: number | null;
  isShortReview?: boolean | null;
  reviewId: string;
  folderId?: string | null;
  videoUrl?: string | null;
  title?: string | null;
  savedAtUtc: string;
}

export interface SmartVideoRuntimeReviewCheckpointInput {
  kind: SmartVideoRuntimeReviewKind;
  index: number | null;
  isShortReview?: boolean | null;
  reviewId: string;
  folderId?: string | null;
  videoUrl?: string | null;
  title?: string | null;
  savedAtUtc?: string | null;
}

export interface SmartVideoRuntimeCheckpointBuildResult {
  step: string;
  stepOutputJson: string;
  checkpoint: SmartVideoRuntimeReviewCheckpoint;
}

export interface SmartVideoRuntimeReviewCheckpointRequestFields {
  smartVideoCheckpointKind: SmartVideoRuntimeReviewKind;
  smartVideoCheckpointIndex: number | null;
}

export interface SmartVideoRuntimeCheckpointState {
  main: SmartVideoRuntimeReviewCheckpoint | null;
  shorts: Map<number, SmartVideoRuntimeReviewCheckpoint>;
  hasMain: boolean;
  hasShort(index: number): boolean;
}

export function savedReviewStepName(kind: SmartVideoRuntimeReviewKind, index: number | null): string {
  if (kind === 'main') {
    return 'SavedReviewMain';
  }

  return `SavedReviewShort${Math.max(0, Math.trunc(Number(index ?? 0))) + 1}`;
}

export function buildSmartVideoRuntimeReviewCheckpointRequestFields(
  kind: SmartVideoRuntimeReviewKind,
  index: number | null
): SmartVideoRuntimeReviewCheckpointRequestFields {
  return {
    smartVideoCheckpointKind: kind,
    smartVideoCheckpointIndex: kind === 'main' ? null : Math.max(0, Math.trunc(Number(index ?? 0)))
  };
}

export function buildSmartVideoRuntimeReviewCheckpoint(
  input: SmartVideoRuntimeReviewCheckpointInput
): SmartVideoRuntimeCheckpointBuildResult {
  const checkpoint: SmartVideoRuntimeReviewCheckpoint = {
    kind: input.kind,
    index: input.kind === 'main' ? null : Math.max(0, Math.trunc(Number(input.index ?? 0))),
    isShortReview: input.isShortReview ?? (input.kind === 'short'),
    reviewId: input.reviewId,
    folderId: input.folderId ?? null,
    videoUrl: input.videoUrl ?? null,
    title: input.title ?? null,
    savedAtUtc: input.savedAtUtc || new Date().toISOString()
  };

  return {
    step: savedReviewStepName(checkpoint.kind, checkpoint.index),
    stepOutputJson: JSON.stringify(checkpoint),
    checkpoint
  };
}

export function parseSmartVideoRuntimeCheckpointResources(
  checkpointResourcesJson: string | null | undefined
): SmartVideoRuntimeCheckpointState {
  const main = { value: null as SmartVideoRuntimeReviewCheckpoint | null };
  const shorts = new Map<number, SmartVideoRuntimeReviewCheckpoint>();
  const resources = parseRecord(checkpointResourcesJson);

  for (const [step, rawOutput] of Object.entries(resources)) {
    const checkpoint = normalizeCheckpoint(rawOutput);
    if (!checkpoint) continue;

    if (step === 'SavedReviewMain' && checkpoint.kind === 'main') {
      main.value = checkpoint;
      continue;
    }

    const shortMatch = /^SavedReviewShort(\d+)$/.exec(step);
    if (shortMatch && checkpoint.kind === 'short' && checkpoint.index !== null) {
      shorts.set(checkpoint.index, checkpoint);
    }
  }

  return {
    main: main.value,
    shorts,
    hasMain: Boolean(main.value),
    hasShort(index: number): boolean {
      return shorts.has(index);
    }
  };
}

function parseRecord(value: string | null | undefined): Record<string, unknown> {
  if (!value?.trim()) return {};

  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed as Record<string, unknown>
      : {};
  } catch {
    return {};
  }
}

function normalizeCheckpoint(raw: unknown): SmartVideoRuntimeReviewCheckpoint | null {
  const parsed = typeof raw === 'string' ? parseJson(raw) : raw;
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

  const record = parsed as Record<string, unknown>;
  const kind = record['kind'];
  const reviewId = cleanString(record['reviewId']);
  if ((kind !== 'main' && kind !== 'short') || !reviewId) return null;

  const index = kind === 'main' ? null : normalizeIndex(record['index']);
  if (kind === 'short' && index === null) return null;

  return {
    kind,
    index,
    isShortReview: typeof record['isShortReview'] === 'boolean'
      ? record['isShortReview']
      : kind === 'short',
    reviewId,
    folderId: cleanString(record['folderId']),
    videoUrl: cleanString(record['videoUrl']),
    title: cleanString(record['title']),
    savedAtUtc: cleanString(record['savedAtUtc']) || new Date(0).toISOString()
  };
}

function parseJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function cleanString(value: unknown): string | null {
  const text = typeof value === 'string' ? value.trim() : '';
  return text || null;
}

function normalizeIndex(value: unknown): number | null {
  const number = Number(value);
  if (!Number.isFinite(number) || number < 0) return null;
  return Math.trunc(number);
}
